import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { buildIndex, domainIcon, filterUnits, hhmm } from '@jobfair/shared';
import { env } from '../lib/env';
import { domainColor, heDate } from '../lib/format';
import { useDebouncedEffect, useFavorites } from '../lib/hooks';
import { KEYS, session } from '../lib/storage';
import { toast } from '../lib/toast';
import { useTrack } from '../lib/trackerContext';
import type { Access, LiveState, Phase, Site as SiteContent, Unit } from '../types';
import { ConsultBooth, ConsultDrawer } from './Consult';
import { Icon } from './Icon';
import { HeroBanner, hasPhotoHeader } from './HeroBanner';
import { StatusPanel } from './StatusPanel';
import { Timeline } from './Timeline';
import { UnitCard } from './UnitCard';
import { UnitDrawer } from './UnitDrawer';

interface Filters {
  q: string;
  dom: string;
  reg: string;
  favOnly: boolean;
}

/** Search and filters survive going to Teams and coming back in the same tab. */
const loadFilters = (): Filters => {
  const f = session.json<Partial<Filters>>(KEYS.filters, {});
  return { q: f.q ?? '', dom: f.dom ?? '', reg: f.reg ?? '', favOnly: !!f.favOnly };
};

interface Props {
  site: SiteContent;
  st: LiveState;
  ph: Phase;
  units: Unit[];
  /** True when state.json hasn't refreshed for a while. */
  stale: boolean;
}

export function Site({ site, st, ph, units, stale }: Props) {
  const track = useTrack();
  const [favs, toggleFav] = useFavorites();
  const [filters, setFilters] = useState<Filters>(loadFilters);
  const { q, dom, reg, favOnly } = filters;
  const [openId, setOpenId] = useState<string | null>(null);
  const [consultOpen, setConsultOpen] = useState(false);

  useEffect(() => {
    session.set(KEYS.filters, JSON.stringify(filters));
  }, [filters]);

  // Drop a filter whose value no longer exists after a content update.
  useEffect(() => {
    setFilters((f) => ({
      ...f,
      dom: f.dom && !site.domains.includes(f.dom) ? '' : f.dom,
      reg: f.reg && !site.regions.includes(f.reg) ? '' : f.reg,
    }));
  }, [site.domains, site.regions]);

  useDebouncedEffect(
    () => {
      if (q.trim()) track('search', { value: q.trim() });
    },
    [q],
    900,
  );

  const index = useMemo(() => buildIndex(units), [units]);
  const results = useMemo(
    () =>
      filterUnits(index, {
        query: q,
        domain: dom,
        region: reg,
        favoritesOnly: favOnly,
        favorites: favs,
      }),
    [index, q, dom, reg, favOnly, favs],
  );
  const favCount = useMemo(() => units.filter((u) => favs.has(u.id)).length, [units, favs]);
  const openUnit = units.find((u) => u.id === openId) ?? null;

  useEffect(() => {
    if (openId && !openUnit) {
      setOpenId(null);
      toast('היחידה שפתחתם הוסרה מהיריד.', 'warn');
    }
  }, [openId, openUnit]);

  const r1 = ph.valid ? hhmm(ph.R[0]!.start) : st.rounds[0].start;
  const ended = ph.state === 'ended';
  const access = useMemo<Access>(
    () => ({ teamsOpen: ph.teamsOpen, ended, r1 }),
    [ph.teamsOpen, ended, r1],
  );

  const set = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));
  const filtersOn = !!(q || dom || reg || favOnly);
  const clear = () => setFilters({ q: '', dom: '', reg: '', favOnly: false });
  const count = (field: 'domain' | 'region', v: string) =>
    units.filter((u) => u[field] === v).length;
  const usedDomains = site.domains.filter((d) => count('domain', d) > 0 || d === dom);

  const onFav = useCallback(
    (id: string) => track(toggleFav(id) ? 'favorite_add' : 'favorite_remove', { unitId: id }),
    [toggleFav, track],
  );
  const onOpen = useCallback(
    (id: string) => {
      setOpenId(id);
      track('unit_open', { unitId: id });
    },
    [track],
  );

  const photo = hasPhotoHeader(site);
  const hours = ph.valid ? `${st.convStart}–${hhmm(ph.R[2]!.end)}` : '';
  const live = ph.state === 'keynote' || ph.state === 'sessions';

  return (
    <div>
      <a className="skip" href="#units">
        דילוג לרשימת היחידות
      </a>
      <div className="fair-stage">
        <header className="s-header">
          <div className="wrap s-header-in">
            <div className="logos">
              {site.logoMoj ? (
                <img className="site-logo" src={site.logoMoj} alt="משרד המשפטים" />
              ) : (
                <div className="logo-slot">לוגו משרד המשפטים</div>
              )}
              {site.logoCodex ? (
                <img className="site-logo" src={site.logoCodex} alt="קודקס" />
              ) : null}
            </div>
            <span className="hdr-note">אין צורך בהרשמה כדי להשתתף</span>
          </div>
        </header>
        {stale && (
          <div className="announce stale" role="status">
            <div className="wrap announce-in">
              <Icon n="alert" />
              החיבור לשרת נותק. המידע המוצג עשוי להיות לא מעודכן, ואנחנו מנסים להתחבר מחדש.
            </div>
          </div>
        )}
        {st.announcement && (
          <div className="announce" role="status">
            <div className="wrap announce-in">
              <Icon n="info" />
              {st.announcement}
            </div>
          </div>
        )}
        <HeroBanner site={site} live={live} />
        <section className="hero">
          <div className="wrap hero-grid no-video">
            <div className={'hero-intro' + (photo ? ' lead-only' : '')}>
              {photo && (
                <h1 id="fair-title" className="sr-only">
                  {site.fairName}
                  <span>של {site.org}</span>
                </h1>
              )}
              {!photo && (
                <div className="hero-meta">
                  <span className="mchip" style={{ '--ac': 'var(--c1)' } as CSSProperties}>
                    <Icon n="cal" s={16} />
                    {heDate(st.date)}
                  </span>
                  {hours && (
                    <span className="mchip num" style={{ '--ac': 'var(--c0)' } as CSSProperties}>
                      <Icon n="clock" s={16} />
                      {hours}
                    </span>
                  )}
                  <span className="mchip" style={{ '--ac': 'var(--c2)' } as CSSProperties}>
                    <Icon n="users" s={16} />
                    {units.length} יחידות · {usedDomains.length} תחומים
                  </span>
                </div>
              )}
              <p className="hero-lead">{site.intro}</p>
            </div>
            <StatusPanel site={site} st={st} ph={ph} />
            <ol className="steps">
              <li style={{ '--ac': 'var(--c1)' } as CSSProperties}>
                <span className="n">1</span>
                <span>
                  <b>מכירים את היחידות</b>חפשו, קראו ושמרו מועדפים כבר עכשיו.
                </span>
              </li>
              <li style={{ '--ac': 'var(--c3)' } as CSSProperties}>
                <span className="n">2</span>
                <span>
                  <b>מצטרפים להתכנסות</b>וובינר משותף לכולם ב-
                  <span className="num">{st.convStart}</span>.
                </span>
              </li>
              <li style={{ '--ac': 'var(--c0)' } as CSSProperties}>
                <span className="n">3</span>
                <span>
                  <b>בוחרים מפגשים</b>שלושה סבבים מ-<span className="num">{r1}</span>, בלי הרשמה.
                </span>
              </li>
            </ol>
          </div>
        </section>
        <Timeline st={st} ph={ph} />
        <ConsultBooth site={site} st={st} ph={ph} onOpen={() => setConsultOpen(true)} />
      </div>

      <div className="toolbar" role="search">
        <div className="wrap toolbar-in">
          <div className="search">
            <span className="s-ic">
              <Icon n="search" />
            </span>
            <label className="sr-only" htmlFor="q">
              חיפוש יחידה
            </label>
            <input
              id="q"
              type="search"
              placeholder="חיפוש לפי שם יחידה, נושא או מילה מהתיאור"
              value={q}
              onChange={(e) => set({ q: e.target.value })}
              autoComplete="off"
            />
            {q && (
              <button
                className="icon-btn s-clear"
                onClick={() => {
                  set({ q: '' });
                  document.getElementById('q')?.focus();
                }}
                aria-label="ניקוי החיפוש"
              >
                <Icon n="close" s={16} />
              </button>
            )}
          </div>
          <label className="sr-only" htmlFor="f-reg">
            סינון לפי אזור
          </label>
          <select
            id="f-reg"
            className="select"
            value={reg}
            onChange={(e) => {
              set({ reg: e.target.value });
              if (e.target.value) track('filter', { value: `region:${e.target.value}` });
            }}
          >
            <option value="">כל האזורים</option>
            {site.regions.map((d) => (
              <option key={d} value={d}>
                {d} ({count('region', d)})
              </option>
            ))}
          </select>
          <button
            className="fav-toggle"
            aria-pressed={favOnly ? 'true' : 'false'}
            onClick={() => {
              set({ favOnly: !favOnly });
              if (!favOnly) track('filter', { value: 'favorites' });
            }}
          >
            <Icon n="star" fill={favOnly} />
            המועדפים שלי<span className="cnt num">{favCount}</span>
          </button>
        </div>
      </div>

      <main id="units" className="wrap" tabIndex={-1}>
        <div className="domchips" role="group" aria-label="סינון לפי תחום">
          <button
            className="dchip"
            aria-pressed={!dom ? 'true' : 'false'}
            onClick={() => set({ dom: '' })}
            style={{ '--ac': 'var(--c1)' } as CSSProperties}
          >
            <span className="dchip-ic">
              <Icon n="grid" s={14} />
            </span>
            כל התחומים<span className="cnt">{units.length}</span>
          </button>
          {usedDomains.map((d) => (
            <button
              key={d}
              className="dchip"
              aria-pressed={dom === d ? 'true' : 'false'}
              onClick={() => {
                set({ dom: dom === d ? '' : d });
                if (dom !== d) track('filter', { value: `domain:${d}` });
              }}
              style={{ '--ac': domainColor(site.domains, d) } as CSSProperties}
            >
              <span className="dchip-ic">
                <Icon n={domainIcon(d)} s={14} />
              </span>
              {d}
              <span className="cnt">{count('domain', d)}</span>
            </button>
          ))}
        </div>

        <div className="result-line">
          <h2 aria-live="polite">
            {filtersOn ? `נמצאו ${results.length} יחידות` : `כל היחידות (${units.length})`}
          </h2>
          {filtersOn && (
            <div className="chips">
              {q && (
                <span className="chip">
                  "{q}"
                  <button aria-label="הסרת החיפוש" onClick={() => set({ q: '' })}>
                    <Icon n="close" s={13} />
                  </button>
                </span>
              )}
              {dom && (
                <span className="chip">
                  {dom}
                  <button aria-label="הסרת סינון התחום" onClick={() => set({ dom: '' })}>
                    <Icon n="close" s={13} />
                  </button>
                </span>
              )}
              {reg && (
                <span className="chip">
                  {reg}
                  <button aria-label="הסרת סינון האזור" onClick={() => set({ reg: '' })}>
                    <Icon n="close" s={13} />
                  </button>
                </span>
              )}
              {favOnly && (
                <span className="chip">
                  מועדפים בלבד
                  <button aria-label="הצגת כל היחידות" onClick={() => set({ favOnly: false })}>
                    <Icon n="close" s={13} />
                  </button>
                </span>
              )}
              <button className="btn btn-quiet btn-sm" onClick={clear}>
                ניקוי הכל
              </button>
            </div>
          )}
        </div>

        {results.length ? (
          <div className="grid">
            {results.map((u, i) => (
              <UnitCard
                key={u.id}
                idx={i}
                u={u}
                access={access}
                fav={favs.has(u.id)}
                onFav={onFav}
                onOpen={onOpen}
              />
            ))}
          </div>
        ) : (
          <div className="empty">
            <Icon n={favOnly && !favCount ? 'star' : 'search'} s={30} />
            {favOnly && !favCount ? (
              <>
                <h3>עוד לא שמרתם מועדפים</h3>
                <p>
                  לחצו על הכוכב בכרטיסיית יחידה כדי לשמור אותה. המועדפים נשמרים בדפדפן הזה, בלי צורך
                  בהרשמה.
                </p>
              </>
            ) : (
              <>
                <h3>לא מצאנו יחידות שמתאימות לחיפוש</h3>
                <p>נסו מילה אחרת, למשל שם תחום כמו "פלילי" או "חקיקה", או נקו את הסינונים.</p>
              </>
            )}
            <button className="btn btn-primary" onClick={clear}>
              ניקוי החיפוש והסינונים
            </button>
          </div>
        )}
      </main>

      <footer className="s-footer">
        <div className="wrap">
          <div className="f-row">
            <span>הגשת מועמדות להתמחות מתבצעת במערכת קודקס ולא באתר היריד.</span>
            {env.demo && <span>רשימת היחידות והתכנים בגרסה זו הם נתוני הדגמה.</span>}
          </div>
          <nav className="f-links" aria-label="מידע וקשר">
            {site.accUrl && (
              <a href={site.accUrl} target="_blank" rel="noopener">
                הצהרת נגישות
              </a>
            )}
            {site.privacyUrl && (
              <a href={site.privacyUrl} target="_blank" rel="noopener">
                מדיניות פרטיות
              </a>
            )}
            {(site.consult.email || site.consult.phone) && (
              <span>
                יצירת קשר: {site.consult.email && <span className="sel">{site.consult.email}</span>}
                {site.consult.email && site.consult.phone ? ' · ' : ''}
                {site.consult.phone && <span className="sel">{site.consult.phone}</span>}
              </span>
            )}
          </nav>
        </div>
      </footer>

      {openUnit && (
        <UnitDrawer
          u={openUnit}
          site={site}
          st={st}
          ph={ph}
          fav={favs.has(openUnit.id)}
          onFav={onFav}
          onClose={() => setOpenId(null)}
          onKeyword={(k) => {
            setOpenId(null);
            set({ q: k });
          }}
        />
      )}
      {consultOpen && site.consult.enabled && (
        <ConsultDrawer site={site} st={st} ph={ph} onClose={() => setConsultOpen(false)} />
      )}
    </div>
  );
}
